import { Connection, PublicKey, LAMPORTS_PER_SOL, Transaction } from "@solana/web3.js";
import { AnchorProvider } from "@coral-xyz/anchor";
import { MoonRunClient, TradeEventData, LockEventData, MigrationEventData, VoteEventData, VoteResultEventData } from "./anchor/program-client.ts";
import { supabase } from "../integrations/supabase/client.ts";
import { Coin } from "../hooks/use-realtime-coins.ts";
import { BlockchainEventMonitor } from "./blockchain-event-monitor.ts";

// Type alias for TransactionSignature
type TransactionSignature = string;
import { PROGRAM_IDS } from "./anchor/idl-types.ts";

declare const process: {
  env: {
    VITE_SOLANA_RPC_URL?: string;
  };
};

export interface BlockchainEvent {
  type: "token_created" | "trade_executed" | "vote_cast" | "pool_updated" | "connection_status" | "connection_failed";
  data: any;
  signature: string;
  timestamp: number;
}

export interface TransactionStatus {
  signature: string;
  status: "pending" | "confirmed" | "failed";
  error?: string;
  confirmedAt?: Date;
}

export class SolanaEventListener {
  private connection: Connection;
  private moonRunClient: MoonRunClient;
  private monitor: BlockchainEventMonitor;
  private eventListenerIds: Map<string, number[]>;
  private transactionStatuses: Map<TransactionSignature, TransactionStatus>;
  private eventHandlers: Map<string, ((event: BlockchainEvent) => void)[]>;
  private isConnected: boolean = false;
  private reconnectAttempts: number = 0;
  private maxReconnectAttempts: number = 10;
  private reconnectDelay: number = 5000;
  private heartbeatInterval: NodeJS.Timeout | null = null;

  constructor(connection: Connection, moonRunClient: MoonRunClient) {
    this.connection = connection;
    this.moonRunClient = moonRunClient;
    this.monitor = new BlockchainEventMonitor();
    this.eventListenerIds = new Map();
    this.transactionStatuses = new Map();
    this.eventHandlers = new Map();
  }

  isHealthy(): boolean {
    return this.isConnected && this.eventListenerIds.size > 0;
  }

  // Start listening to program events
  async startListening() {
    console.log("Starting Anchor event listeners...");

    // Add connection error listener
    this.connection.on('disconnect', () => {
      this.isConnected = false;
      this.reconnect();
    });

    await this.setupAnchorEventListeners();

    this.isConnected = true;
    this.reconnectAttempts = 0;
    this.emitEvent({
      type: "connection_status",
      data: { status: "connected" },
      signature: "",
      timestamp: Date.now(),
    });

    // Start heartbeat
    this.heartbeatInterval = setInterval(() => {
      if (!this.isHealthy()) {
        this.reconnect();
      }
    }, 30000);

    console.log("Anchor event listeners started");
  }

  private async reconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error("Max reconnection attempts reached");
      this.emitEvent({
        type: "connection_failed",
        data: { attempts: this.reconnectAttempts },
        signature: "",
        timestamp: Date.now(),
      });
      return;
    }

    this.reconnectAttempts++;
    console.log(`Reconnecting... (attempt ${this.reconnectAttempts})`);

    this.stopListening();
    await new Promise(resolve => setTimeout(resolve, this.reconnectDelay));

    try {
      await this.startListening();
    } catch (error) {
      console.error("Reconnection failed:", error);
      await this.reconnect(); // Retry
    }
  }

  // Setup Anchor event listeners
  private async setupAnchorEventListeners() {
    try {
      const bondingIds = this.moonRunClient.setupBondingCurveEventListeners({
        onTrade: (event, slot, signature) => this.handleTradeEvent(event),
        onLock: (event, slot, signature) => this.handleLockEvent(event),
        onMigration: (event, slot, signature) => this.handleMigrationEvent(event),
      });
      this.eventListenerIds.set('bondingCurve', bondingIds);

      const governanceIds = this.moonRunClient.setupGovernanceEventListeners({
        onVote: (event, slot, signature) => this.handleVoteEvent(event),
        onVoteResult: (event, slot, signature) => this.handleVoteResultEvent(event),
      });
      this.eventListenerIds.set('governance', governanceIds);
    } catch (error) {
      console.error("Failed to setup Anchor event listeners:", error);
      this.reconnect();
    }
  }

  // Handle trade event
  private async handleTradeEvent(event: TradeEventData) {
    const blockchainEvent: BlockchainEvent = {
      type: "trade_executed",
      data: {
        tokenMint: event.pool.toString(),
        tradeType: event.isBuy ? "buy" : "sell",
        amount: event.tokenAmount.toNumber(),
        solAmount: event.solAmount.toNumber() / LAMPORTS_PER_SOL,
        price: event.solAmount.toNumber() / event.tokenAmount.toNumber(),
        newMarketCap: event.marketCap.toNumber() / LAMPORTS_PER_SOL,
        trader: event.trader.toString(),
        timestamp: event.timestamp.toNumber() * 1000,
      },
      signature: "event_" + Date.now(),
      timestamp: event.timestamp.toNumber() * 1000,
    };
    this.emitEvent(blockchainEvent);
    await this.syncWithDatabase(blockchainEvent);
  }

  // Handle lock event
  private async handleLockEvent(event: LockEventData) {
    const blockchainEvent: BlockchainEvent = {
      type: "pool_updated",
      data: {
        tokenMint: event.pool.toString(),
        isLocked: true,
        timestamp: event.timestamp.toNumber() * 1000,
      },
      signature: "event_" + Date.now(),
      timestamp: event.timestamp.toNumber() * 1000,
    };
    this.emitEvent(blockchainEvent);
    await this.syncWithDatabase(blockchainEvent);
  }

  // Handle migration event
  private async handleMigrationEvent(event: MigrationEventData) {
    const blockchainEvent: BlockchainEvent = {
      type: "pool_updated",
      data: {
        tokenMint: event.pool.toString(),
        migrated: true,
        timestamp: event.timestamp.toNumber() * 1000,
      },
      signature: "event_" + Date.now(),
      timestamp: event.timestamp.toNumber() * 1000,
    };
    this.emitEvent(blockchainEvent);
    await this.syncWithDatabase(blockchainEvent);
  }

  // Handle vote event
  private async handleVoteEvent(event: VoteEventData) {
    const blockchainEvent: BlockchainEvent = {
      type: "vote_cast",
      data: {
        tokenMint: event.governance.toString(),
        voter: event.voter.toString(),
        direction: event.direction,
        weight: 0,
        isStreamer: false,
        timestamp: event.timestamp.toNumber() * 1000,
      },
      signature: "event_" + Date.now(),
      timestamp: event.timestamp.toNumber() * 1000,
    };
    this.emitEvent(blockchainEvent);
    await this.syncWithDatabase(blockchainEvent);
  }

  // Handle vote result event
  private async handleVoteResultEvent(event: VoteResultEventData) {
    const blockchainEvent: BlockchainEvent = {
      type: "pool_updated",
      data: {
        tokenMint: event.governance.toString(),
        shouldUnlock: event.shouldUnlock,
        timestamp: event.timestamp.toNumber() * 1000,
      },
      signature: "event_" + Date.now(),
      timestamp: event.timestamp.toNumber() * 1000,
    };
    this.emitEvent(blockchainEvent);
    await this.syncWithDatabase(blockchainEvent);
  }

  // Stop all listeners
  stopListening() {
    console.log("Stopping Solana event listeners...");
    this.eventListenerIds.forEach((ids, key) => {
      ids.forEach(id => {
        try {
          this.moonRunClient.removeEventListener(id);
        } catch (error) {
          console.error(`Error removing listener ${id} for ${key}:`, error);
        }
      });
    });
    this.eventListenerIds.clear();
    this.isConnected = false;
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    console.log("Solana event listeners stopped");
  }


  // Emit event to subscribers
  private emitEvent(event: BlockchainEvent) {
    const startTime = Date.now();
    this.monitor.logEvent(event);
    const handlers = this.eventHandlers.get(event.type) || [];
    handlers.forEach((handler) => {
      try {
        handler(event);
      } catch (error) {
        console.warn(`Warning: Event processing failure for ${event.type}:`, error);
      }
    });
    const processingTime = Date.now() - startTime;
    if (processingTime > 100) { // Log if processing takes more than 100ms
      console.log(`[Performance] Event ${event.type} processing took ${processingTime}ms`);
    }
  }

  // Add event listener
  onEvent(eventType: BlockchainEvent["type"], handler: (event: BlockchainEvent) => void) {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)?.push(handler);
  }

  // Remove event listener
  offEvent(eventType: BlockchainEvent["type"], handler: (event: BlockchainEvent) => void) {
    const handlers = this.eventHandlers.get(eventType) || [];
    const index = handlers.indexOf(handler);
    if (index !== -1) {
      handlers.splice(index, 1);
    }
  }

  // Get transaction status
  getTransactionStatus(signature: TransactionSignature): TransactionStatus | undefined {
    return this.transactionStatuses.get(signature);
  }

  // Sync blockchain events with database
  private async syncWithDatabase(event: BlockchainEvent) {
    const startTime = Date.now();
    try {
      switch (event.type) {
        case "token_created":
          await this.syncTokenCreated(event);
          break;
        case "trade_executed":
          await this.syncTradeExecuted(event);
          break;
        case "vote_cast":
          await this.syncVoteCast(event);
          break;
        case "pool_updated":
          await this.syncPoolUpdated(event);
          break;
      }
      const syncTime = Date.now() - startTime;
      if (syncTime > 1000) { // Log slow database syncs > 1 second
        console.log(`[Performance] Slow database sync for ${event.type}: ${syncTime}ms`);
      }
    } catch (error) {
      console.warn(`Warning: Database sync failure for ${event.type}:`, error);
    }
  }

  // Sync token created event
  private async syncTokenCreated(event: BlockchainEvent) {
    const tokenData = event.data;

    // Fetch on-chain token metadata
    const tokenConfig = await this.moonRunClient.token.getTokenConfigData(new PublicKey(tokenData.mintAddress));
    if (!tokenConfig) {
      console.error("Failed to fetch token config for token created sync");
      return;
    }

    // Update or create coin in database
    const { data: existingCoin, error: fetchError } = await supabase
      .from("coins")
      .select("*")
      .eq("mint_address", tokenData.mintAddress)
      .single();

    if (fetchError && fetchError.code !== "PGRST116") {
      console.error("Error fetching existing coin:", fetchError);
      return;
    }

    const coinData: Partial<Coin> = {
      name: tokenConfig.name,
      ticker: tokenConfig.symbol,
      mint_address: tokenData.mintAddress,
      creator_wallet: tokenConfig.creator.toString(),
      created_at: Math.floor(Date.now() / 1000),
      price: 0.0001,
      market_cap: 1000,
      holders: 1,
      status: "active",
    };

    if (existingCoin) {
      // Update existing coin
      const { error: updateError } = await supabase
        .from("coins")
        .update(coinData)
        .eq("id", existingCoin.id);

      if (updateError) {
        console.error("Error updating coin:", updateError);
      }
    } else {
      // Create new coin
      const { data: newCoin, error: createError } = await supabase
        .from("coins")
        .insert(coinData)
        .select()
        .single();

      if (createError) {
        console.error("Error creating coin:", createError);
      } else {
        console.log("Created new coin in database:", newCoin.id);
      }
    }
  }

  // Sync trade executed event
  private async syncTradeExecuted(event: BlockchainEvent) {
    const tradeData = event.data;

    // Query pool data for accurate reserves/market cap
    const poolData = await this.moonRunClient.bondingCurve.getPoolData(new PublicKey(tradeData.tokenMint));
    if (!poolData) {
      console.error("Failed to fetch pool data for trade sync");
      return;
    }

    // Find coin by mint address
    const { data: coin, error: coinError } = await supabase
      .from("coins")
      .select("*")
      .eq("mint_address", tradeData.tokenMint)
      .single();

    if (coinError) {
      console.error("Error finding coin for trade:", coinError);
      return;
    }

    // Update coin data
    const { error: updateError } = await supabase
      .from("coins")
      .update({
        price: poolData.marketCap.toNumber() / poolData.tokenReserve.toNumber(),
        market_cap: poolData.marketCap.toNumber() / LAMPORTS_PER_SOL,
        updated_at: Math.floor(Date.now() / 1000),
      })
      .eq("id", coin.id);

    if (updateError) {
      console.error("Error updating coin after trade:", updateError);
    }

    // Create trade record
    const { error: tradeError } = await supabase
      .from("trades")
      .insert({
        coin_id: coin.id,
        trade_type: tradeData.tradeType,
        amount: tradeData.amount,
        price: tradeData.price,
        tx_hash: event.signature,
        created_at: Math.floor(Date.now() / 1000),
        status: "completed",
      });

    if (tradeError) {
      console.error("Error creating trade record:", tradeError);
    }
  }

  // Sync vote cast event
  private async syncVoteCast(event: BlockchainEvent) {
    const voteData = event.data;

    // Fetch current vote tallies
    const governanceData = await this.moonRunClient.governance.getGovernanceConfigData(new PublicKey(voteData.tokenMint));
    if (!governanceData) {
      console.error("Failed to fetch governance config for vote sync");
      return;
    }

    // Find coin by mint address
    const { data: coin, error: coinError } = await supabase
      .from("coins")
      .select("*")
      .eq("mint_address", voteData.tokenMint)
      .single();

    if (coinError) {
      console.error("Error finding coin for vote:", coinError);
      return;
    }

    // Update governance data
    const direction = voteData.direction ? "unlock" : "lock";
    const voteType = voteData.isStreamer ? "streamer" : "user";

    const { error: voteError } = await supabase
      .from("votes")
      .insert({
        coin_id: coin.id,
        voter_wallet: voteData.voter,
        direction,
        weight: voteData.weight,
        vote_type: voteType,
        created_at: Math.floor(Date.now() / 1000),
      });

    if (voteError) {
      console.error("Error recording vote:", voteError);
    }
  }

  // Sync pool updated event
  private async syncPoolUpdated(event: BlockchainEvent) {
    const poolData = event.data;

    // Fetch accurate pool state
    const onChainPoolData = await this.moonRunClient.bondingCurve.getPoolData(new PublicKey(poolData.tokenMint));
    if (!onChainPoolData) {
      console.error("Failed to fetch pool data for pool update sync");
      return;
    }

    // Find coin by mint address
    const { data: coin, error: coinError } = await supabase
      .from("coins")
      .select("*")
      .eq("mint_address", poolData.tokenMint)
      .single();

    if (coinError) {
      console.error("Error finding coin for pool update:", coinError);
      return;
    }

    // Update coin data with pool information
    const { error: updateError } = await supabase
      .from("coins")
      .update({
        price: onChainPoolData.marketCap.toNumber() / onChainPoolData.tokenReserve.toNumber(),
        market_cap: onChainPoolData.marketCap.toNumber() / LAMPORTS_PER_SOL,
        liquidity: onChainPoolData.solReserve.toNumber() / LAMPORTS_PER_SOL,
        holders: 1,
        updated_at: Math.floor(Date.now() / 1000),
      })
      .eq("id", coin.id);

    if (updateError) {
      console.error("Error updating coin with pool data:", updateError);
    }
  }
}

// Factory function
export function createSolanaEventListener(
  rpcUrl?: string
): SolanaEventListener {
  const connection = new Connection(
    rpcUrl || import.meta.env.VITE_SOLANA_RPC_URL || "https://api.devnet.solana.com",
    "confirmed"
  );
  const wallet = {
    publicKey: new PublicKey("11111111111111111111111111111112"),
    signTransaction: async (tx: Transaction) => tx,
    signAllTransactions: async (txs: Transaction[]) => txs,
  };
  const config = { connection, wallet };
  const moonRunClient = new MoonRunClient(config);
  return new SolanaEventListener(connection, moonRunClient);
}